import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { 
  Download, RefreshCw, Users, TrendingUp, 
  Phone, Mail, Calendar, ChevronDown, ChevronUp,
  Eye, EyeOff, Lock, Plus, Trash2, ToggleLeft, ToggleRight,
  Facebook, Settings, BarChart3, MessageSquareText, Save, RotateCcw,
  Shield, LogOut, UserCog
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies for auth
axios.defaults.withCredentials = true;

// Helper: get admin token from sessionStorage (password or Google)
const getAdminToken = () => sessionStorage.getItem("momento_admin_token") || sessionStorage.getItem("momento_google_token") || "";
const setAdminToken = (token) => sessionStorage.setItem("momento_admin_token", token);
const clearAdminToken = () => {
  sessionStorage.removeItem("momento_admin_token");
  sessionStorage.removeItem("momento_google_token");
};

// Axios config with Bearer token (works for both password + Google session)
const authHeaders = () => {
  const token = getAdminToken();
  return token ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true } : { withCredentials: true };
};

// Format number with commas
const formatNumber = (num) => {
  if (!num) return "0";
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("he-IL", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminDashboard = () => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, true/false = result
  const [currentUser, setCurrentUser] = useState(null);
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [stats, setStats] = useState({ total_leads: 0 });
  const [activeTab, setActiveTab] = useState("leads");
  
  // Pixel management state
  const [pixels, setPixels] = useState([]);
  const [newPixelId, setNewPixelId] = useState("");
  const [newPixelName, setNewPixelName] = useState("");
  const [pixelLoading, setPixelLoading] = useState(false);
  
  // Funnel state
  const [funnel, setFunnel] = useState([]);
  
  // Questions state
  const [questions, setQuestions] = useState({});
  const [editingStep, setEditingStep] = useState(null);
  const [editData, setEditData] = useState({ title: "", subtitle: "", hint: "" });
  
  // Admin emails state
  const [adminEmails, setAdminEmails] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");

  // Check existing session on mount (Google cookie or password token)
  useEffect(() => {
    // Skip auto-check if arriving from OAuth callback (user data passed via state)
    if (location.state?.user) {
      setIsAuthenticated(true);
      setCurrentUser(location.state.user);
      loadAllData();
      return;
    }
    
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, authHeaders());
        setIsAuthenticated(true);
        setCurrentUser(res.data);
        loadAllData();
      } catch (err) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllData = () => {
    fetchData();
    fetchPixels();
    fetchFunnel();
    fetchQuestions();
    fetchAdminEmails();
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin-momento-secure-2024";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // ignore
    }
    clearAdminToken();
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  const authenticate = async () => {
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.token) {
        setAdminToken(res.data.token);
        setIsAuthenticated(true);
        setCurrentUser({ auth_type: "password" });
        loadAllData();
      }
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("יותר מדי ניסיונות. נסו שוב מאוחר יותר");
      } else {
        toast.error("סיסמה שגויה");
      }
    }
  };

  const fetchAdminEmails = async () => {
    try {
      const res = await axios.get(`${API}/admin/admin-emails`, authHeaders());
      setAdminEmails(res.data);
    } catch (err) {
      console.error("Admin emails error:", err);
    }
  };

  const addAdminEmail = async () => {
    if (!newAdminEmail.trim() || !newAdminEmail.includes("@")) {
      toast.error("יש להזין כתובת אימייל תקינה");
      return;
    }
    try {
      await axios.post(`${API}/admin/admin-emails`, { email: newAdminEmail.trim() }, authHeaders());
      toast.success("האדמין נוסף בהצלחה");
      setNewAdminEmail("");
      fetchAdminEmails();
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה בהוספת אדמין");
    }
  };

  const removeAdminEmail = async (email) => {
    if (!window.confirm(`למחוק את ${email} מרשימת האדמינים?`)) return;
    try {
      await axios.delete(`${API}/admin/admin-emails/${encodeURIComponent(email)}`, authHeaders());
      toast.success("האדמין הוסר");
      fetchAdminEmails();
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה במחיקה");
    }
  };

  const fetchFunnel = async () => {
    try {
      const res = await axios.get(`${API}/admin/funnel`, authHeaders());
      setFunnel(res.data.funnel);
    } catch (err) {
      console.error("Funnel error:", err);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/questions`);
      setQuestions(res.data);
    } catch (err) {
      console.error("Questions error:", err);
    }
  };

  const saveQuestion = async (stepNumber) => {
    try {
      await axios.put(`${API}/admin/questions`, {
        step_number: stepNumber,
        title: editData.title,
        subtitle: editData.subtitle,
        hint: editData.hint,
      }, authHeaders());
      toast.success("השאלה עודכנה בהצלחה");
      setEditingStep(null);
      fetchQuestions();
    } catch (err) {
      toast.error("שגיאה בעדכון השאלה");
    }
  };

  const resetQuestion = async (stepNumber) => {
    if (!window.confirm("לשחזר את השאלה לברירת המחדל?")) return;
    try {
      await axios.delete(`${API}/admin/questions/${stepNumber}`, authHeaders());
      toast.success("השאלה שוחזרה לברירת המחדל");
      setEditingStep(null);
      fetchQuestions();
    } catch (err) {
      toast.error("שגיאה בשחזור");
    }
  };

  const startEditing = (stepNumber) => {
    const q = questions[stepNumber] || {};
    setEditData({
      title: q.title || "",
      subtitle: q.subtitle || "",
      hint: q.hint || "",
    });
    setEditingStep(stepNumber);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, authHeaders()),
        axios.get(`${API}/admin/stats`, authHeaders()),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearAdminToken();
        setIsAuthenticated(false);
        toast.error("הסשן פג. אנא התחברו מחדש");
      } else {
        toast.error("שגיאה בטעינת הנתונים");
      }
      console.error(err);
    }
    setLoading(false);
  };

  const fetchPixels = async () => {
    try {
      const res = await axios.get(`${API}/admin/pixels`, authHeaders());
      setPixels(res.data);
    } catch (err) {
      console.error("Error fetching pixels:", err);
    }
  };

  const addPixel = async () => {
    if (!newPixelId.trim()) {
      toast.error("יש להזין מזהה פיקסל");
      return;
    }
    
    setPixelLoading(true);
    try {
      await axios.post(`${API}/admin/pixels`, {
        pixel_id: newPixelId.trim(),
        name: newPixelName.trim()
      }, authHeaders());
      toast.success("הפיקסל נוסף בהצלחה");
      setNewPixelId("");
      setNewPixelName("");
      fetchPixels();
    } catch (err) {
      if (err.response?.data?.detail === "Pixel already exists") {
        toast.error("פיקסל זה כבר קיים במערכת");
      } else {
        toast.error("שגיאה בהוספת הפיקסל");
      }
    }
    setPixelLoading(false);
  };

  const togglePixel = async (pixelId) => {
    try {
      const res = await axios.put(`${API}/admin/pixels/${pixelId}/toggle`, {}, authHeaders());
      toast.success(res.data.active ? "הפיקסל הופעל" : "הפיקסל כובה");
      fetchPixels();
    } catch (err) {
      toast.error("שגיאה בעדכון הפיקסל");
    }
  };

  const deletePixel = async (pixelId) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק את הפיקסל?")) return;
    
    try {
      await axios.delete(`${API}/admin/pixels/${pixelId}`, authHeaders());
      toast.success("הפיקסל נמחק בהצלחה");
      fetchPixels();
    } catch (err) {
      toast.error("שגיאה במחיקת הפיקסל");
    }
  };

  const downloadCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export`, authHeaders());
      const blob = new Blob([res.data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `momento_leads_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      toast.success("הקובץ הורד בהצלחה");
    } catch (err) {
      toast.error("שגיאה בהורדת הקובץ");
    }
  };

  const getBdiLabel = (bdi) => {
    switch (bdi) {
      case "excellent": return "תקין";
      case "medium": return "חריגות קטנות";
      case "poor": return "מורכב";
      default: return bdi || "-";
    }
  };

  // Loading state while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" dir="rtl">
        <div className="animate-spin w-12 h-12 border-4 border-[#C5A45A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" dir="rtl">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Lock className="w-12 h-12 mx-auto text-[#C5A45A] mb-4" />
            <CardTitle className="text-2xl">ממשק ניהול</CardTitle>
            <p className="text-[#64748B]">MOMENTO</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Google Login Button */}
            <Button
              onClick={loginWithGoogle}
              className="w-full h-12 bg-white text-[#0F172A] border-2 border-[#E2E8F0] hover:bg-[#F1F5F9] shadow-sm"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 ml-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              התחברות דרך Google
            </Button>

            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-[#E2E8F0]"></div>
              <span className="text-xs text-[#94A3B8]">או</span>
              <div className="flex-1 h-px bg-[#E2E8F0]"></div>
            </div>

            <Input
              type="password"
              placeholder="סיסמת אדמין"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && authenticate()}
              className="h-12 text-center"
              data-testid="admin-password"
            />
            <Button
              onClick={authenticate}
              className="w-full h-12 bg-[#0F172A] hover:bg-[#1E293B]"
              data-testid="admin-login-btn"
            >
              כניסה עם סיסמה
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      {/* Header */}
      <header className="bg-[#0F172A] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold">ממשק ניהול - MOMENTO</h1>
          <div className="flex items-center gap-3">
            {currentUser?.email && (
              <span className="text-sm text-white/70 hidden sm:inline">
                {currentUser.email}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchData(); fetchPixels(); fetchFunnel(); fetchQuestions(); fetchAdminEmails(); }}
              disabled={loading}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 ml-2 ${loading ? "animate-spin" : ""}`} />
              רענון
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 ml-2" />
              יציאה
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="leads" className="gap-2">
              <Users className="w-4 h-4" />
              לידים
            </TabsTrigger>
            <TabsTrigger value="funnel" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              משפך המרות
            </TabsTrigger>
            <TabsTrigger value="questions" className="gap-2">
              <MessageSquareText className="w-4 h-4" />
              עריכת שאלות
            </TabsTrigger>
            <TabsTrigger value="pixels" className="gap-2">
              <Facebook className="w-4 h-4" />
              ניהול פיקסלים
            </TabsTrigger>
            <TabsTrigger value="admins" className="gap-2">
              <UserCog className="w-4 h-4" />
              ניהול אדמינים
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="p-3 bg-[#C5A45A]/10 rounded-full">
                    <Users className="w-6 h-6 text-[#C5A45A]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748B]">סה"כ לידים</p>
                    <p className="text-3xl font-bold text-[#0F172A]">{stats.total_leads}</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="p-3 bg-[#10B981]/10 rounded-full">
                    <TrendingUp className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748B]">ממוצע חיסכון חודשי</p>
                    <p className="text-3xl font-bold text-[#0F172A]">
                      {leads.length > 0 
                        ? formatNumber(leads.reduce((acc, l) => acc + (l.monthly_savings || 0), 0) / leads.length)
                        : 0} ₪
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="p-3 bg-[#38BDF8]/10 rounded-full">
                    <Calendar className="w-6 h-6 text-[#38BDF8]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748B]">לידים היום</p>
                    <p className="text-3xl font-bold text-[#0F172A]">
                      {leads.filter(l => {
                        const today = new Date().toDateString();
                        return new Date(l.timestamp).toDateString() === today;
                      }).length}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Leads Table */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>רשימת לידים</CardTitle>
                <Button
                  size="sm"
                  onClick={downloadCSV}
                  className="bg-[#C5A45A] hover:bg-[#B8953D]"
                  data-testid="download-csv"
                >
                  <Download className="w-4 h-4 ml-2" />
                  הורדת CSV
                </Button>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right">שם</TableHead>
                      <TableHead className="text-right">טלפון</TableHead>
                      <TableHead className="text-right">סה"כ חובות</TableHead>
                      <TableHead className="text-right">החזר נוכחי</TableHead>
                      <TableHead className="text-right">חיסכון חודשי</TableHead>
                      <TableHead className="text-right">BDI</TableHead>
                      <TableHead className="text-right">תאריך</TableHead>
                      <TableHead className="text-right">פרטים</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <React.Fragment key={lead.id}>
                        <TableRow className="hover:bg-[#F1F5F9]">
                          <TableCell className="font-medium">{lead.name}</TableCell>
                          <TableCell>
                            <a 
                              href={`tel:${lead.phone}`} 
                              className="flex items-center gap-1 text-[#0F172A] hover:text-[#C5A45A]"
                            >
                              <Phone className="w-4 h-4" />
                              {lead.phone}
                            </a>
                          </TableCell>
                          <TableCell>{formatNumber(lead.loans + lead.mortage)} ₪</TableCell>
                          <TableCell>{formatNumber(lead.loans_monthly + lead.mortage_monthly)} ₪</TableCell>
                          <TableCell className="text-[#10B981] font-bold">
                            {formatNumber(lead.monthly_savings)} ₪
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              lead.bdi === "excellent" ? "bg-[#10B981]/10 text-[#10B981]" :
                              lead.bdi === "medium" ? "bg-[#F59E0B]/10 text-[#F59E0B]" :
                              "bg-[#EF4444]/10 text-[#EF4444]"
                            }`}>
                              {getBdiLabel(lead.bdi)}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-[#64748B]">
                            {formatDate(lead.timestamp)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setExpandedRow(expandedRow === lead.id ? null : lead.id)}
                            >
                              {expandedRow === lead.id ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                        {expandedRow === lead.id && (
                          <TableRow>
                            <TableCell colSpan={8} className="bg-[#F8FAFC]">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                                <div>
                                  <p className="text-xs text-[#64748B]">אימייל</p>
                                  <p className="font-medium">{lead.email || "-"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#64748B]">שווי נכס</p>
                                  <p className="font-medium">{formatNumber(lead.asset_value)} ₪</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#64748B]">משכנתא קיימת</p>
                                  <p className="font-medium">{formatNumber(lead.mortage)} ₪</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#64748B]">הכנסה חודשית</p>
                                  <p className="font-medium">{formatNumber(lead.income)} ₪</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#64748B]">צבירה ב-5 שנים</p>
                                  <p className="font-medium text-[#10B981]">{formatNumber(lead.future_value_5y)} ₪</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#64748B]">צבירה ב-10 שנים</p>
                                  <p className="font-medium text-[#10B981]">{formatNumber(lead.future_value_10y)} ₪</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#64748B]">צבירה ב-20 שנים</p>
                                  <p className="font-medium text-[#C5A45A]">{formatNumber(lead.future_value_20y)} ₪</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#64748B]">UTM Source</p>
                                  <p className="font-medium">{lead.utm_source || "-"}</p>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                    {leads.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-[#64748B]">
                          אין לידים עדיין
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Funnel Tab */}
          <TabsContent value="funnel">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#C5A45A]" />
                  משפך המרות - איפה גולשים נוטשים?
                </CardTitle>
              </CardHeader>
              <CardContent>
                {funnel.length === 0 ? (
                  <div className="text-center py-8 text-[#64748B]">
                    אין עדיין נתוני משפך
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(() => {
                      const stepLabels = {
                        1: "שלב 1: בעלות על נכס",
                        2: "שלב 2: שווי הנכס",
                        3: "שלב 3: משכנתא קיימת",
                        4: "שלב 4: סך ההלוואות",
                        5: "שלב 5: החזר חודשי נוכחי",
                        6: "שלב 6: היסטוריית אשראי",
                        7: "שלב 7: הכנסת משק הבית",
                        8: "שלב 8: פרטי קשר",
                        9: "🎯 לידים שהוגשו"
                      };
                      const maxValue = Math.max(...funnel.map(f => f.unique_sessions));
                      const firstStep = funnel.find(f => f.step_number === 1)?.unique_sessions || maxValue || 1;
                      
                      return funnel.map((step) => {
                        const percentage = firstStep > 0 ? (step.unique_sessions / firstStep) * 100 : 0;
                        const isLastStep = step.step_number === 9;
                        return (
                          <div key={step.step_number} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className={`text-sm font-medium ${isLastStep ? "text-[#C5A45A]" : "text-[#0F172A]"}`}>
                                {stepLabels[step.step_number] || `שלב ${step.step_number}`}
                              </span>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-[#64748B]">
                                  {percentage.toFixed(1)}%
                                </span>
                                <span className="text-lg font-bold text-[#0F172A]">
                                  {step.unique_sessions}
                                </span>
                              </div>
                            </div>
                            <div className="h-8 bg-[#F1F5F9] rounded-lg overflow-hidden">
                              <div
                                className={`h-full flex items-center px-3 transition-all duration-500 ${
                                  isLastStep 
                                    ? "bg-gradient-to-l from-[#C5A45A] to-[#D4B36A]" 
                                    : "bg-gradient-to-l from-[#0F172A] to-[#1E293B]"
                                }`}
                                style={{ width: `${Math.max(percentage, 5)}%` }}
                              />
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
                <p className="text-xs text-[#94A3B8] mt-6 text-center">
                  * המספרים מייצגים משתמשים ייחודיים שהגיעו לכל שלב
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Questions Editor Tab */}
          <TabsContent value="questions">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquareText className="w-5 h-5 text-[#C5A45A]" />
                  עריכת טקסטים של השאלות
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((stepNum) => {
                    const q = questions[stepNum] || {};
                    const isEditing = editingStep === stepNum;
                    return (
                      <Card key={stepNum} className={isEditing ? "border-[#C5A45A]" : ""}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-[#C5A45A] text-white flex items-center justify-center font-bold text-sm">
                                {stepNum}
                              </div>
                              <span className="font-semibold text-[#0F172A]">שלב {stepNum}</span>
                            </div>
                            <div className="flex gap-2">
                              {!isEditing && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => startEditing(stepNum)}
                                >
                                  ערוך
                                </Button>
                              )}
                              {isEditing && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => saveQuestion(stepNum)}
                                    className="bg-[#10B981] hover:bg-[#059669]"
                                  >
                                    <Save className="w-4 h-4 ml-1" />
                                    שמור
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingStep(null)}
                                  >
                                    ביטול
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => resetQuestion(stepNum)}
                                    className="text-[#EF4444] hover:text-[#DC2626]"
                                  >
                                    <RotateCcw className="w-4 h-4 ml-1" />
                                    ברירת מחדל
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>

                          {isEditing ? (
                            <div className="space-y-3">
                              <div>
                                <Label className="text-xs text-[#64748B]">כותרת</Label>
                                <Input
                                  value={editData.title}
                                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-[#64748B]">תת-כותרת / תיאור</Label>
                                <Textarea
                                  value={editData.subtitle}
                                  onChange={(e) => setEditData({ ...editData, subtitle: e.target.value })}
                                  className="mt-1 min-h-[60px]"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-[#64748B]">רמז / הערה (אופציונלי)</Label>
                                <Input
                                  value={editData.hint}
                                  onChange={(e) => setEditData({ ...editData, hint: e.target.value })}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <p className="font-bold text-[#0F172A]">{q.title}</p>
                              <p className="text-sm text-[#64748B]">{q.subtitle}</p>
                              {q.hint && <p className="text-xs text-[#94A3B8]">{q.hint}</p>}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <p className="text-xs text-[#94A3B8] mt-4 text-center">
                  * שינויים יופיעו מיידית באתר לאחר שמירה
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pixels Tab */}
          <TabsContent value="pixels">
            <div className="grid gap-6">
              {/* Add Pixel Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Facebook className="w-5 h-5 text-[#1877F2]" />
                    הוספת פיקסל מטא חדש
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <Input
                        placeholder="מזהה פיקסל (Pixel ID)"
                        value={newPixelId}
                        onChange={(e) => setNewPixelId(e.target.value)}
                        className="h-12"
                        data-testid="new-pixel-id"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="שם לזיהוי (אופציונלי)"
                        value={newPixelName}
                        onChange={(e) => setNewPixelName(e.target.value)}
                        className="h-12"
                        data-testid="new-pixel-name"
                      />
                    </div>
                    <Button
                      onClick={addPixel}
                      disabled={pixelLoading}
                      className="h-12 px-6 bg-[#1877F2] hover:bg-[#166FE5]"
                      data-testid="add-pixel-btn"
                    >
                      <Plus className="w-4 h-4 ml-2" />
                      הוסף פיקסל
                    </Button>
                  </div>
                  <p className="text-xs text-[#64748B] mt-3">
                    הפיקסל ישלח אירועי PageView בכל מסכי השאלון ואירוע Lead במסך הסיום
                  </p>
                </CardContent>
              </Card>

              {/* Pixels List */}
              <Card>
                <CardHeader>
                  <CardTitle>פיקסלים פעילים</CardTitle>
                </CardHeader>
                <CardContent>
                  {pixels.length === 0 ? (
                    <div className="text-center py-8 text-[#64748B]">
                      <Facebook className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>לא הוגדרו פיקסלים עדיין</p>
                      <p className="text-sm mt-1">הוסיפו פיקסל מטא כדי לעקוב אחרי המרות</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pixels.map((pixel) => (
                        <div
                          key={pixel.id}
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            pixel.active 
                              ? "bg-[#10B981]/5 border-[#10B981]/20" 
                              : "bg-[#F1F5F9] border-[#E2E8F0]"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Facebook className={`w-5 h-5 ${pixel.active ? "text-[#1877F2]" : "text-[#94A3B8]"}`} />
                            <div>
                              <p className={`font-medium ${pixel.active ? "text-[#0F172A]" : "text-[#64748B]"}`}>
                                {pixel.name}
                              </p>
                              <p className="text-sm text-[#94A3B8] font-mono">{pixel.pixel_id}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              pixel.active 
                                ? "bg-[#10B981]/10 text-[#10B981]" 
                                : "bg-[#94A3B8]/10 text-[#94A3B8]"
                            }`}>
                              {pixel.active ? "פעיל" : "מושבת"}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => togglePixel(pixel.pixel_id)}
                              className="h-9 w-9 p-0"
                            >
                              {pixel.active ? (
                                <ToggleRight className="w-5 h-5 text-[#10B981]" />
                              ) : (
                                <ToggleLeft className="w-5 h-5 text-[#94A3B8]" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deletePixel(pixel.pixel_id)}
                              className="h-9 w-9 p-0 text-[#EF4444] hover:text-[#DC2626] hover:bg-[#EF4444]/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="bg-[#F0F9FF] border-[#38BDF8]/20">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Settings className="w-5 h-5 text-[#38BDF8] flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-[#0F172A] mb-1">איך זה עובד?</p>
                      <ul className="text-[#64748B] space-y-1">
                        <li>• <strong>PageView</strong> - נשלח בכל מסך של השאלון</li>
                        <li>• <strong>Lead</strong> - נשלח כשהמשתמש מגיע לעמוד התוצאות</li>
                        <li>• ניתן להוסיף מספר פיקסלים - כולם יפעלו במקביל</li>
                        <li>• השבתת פיקסל תפסיק את השליחה מיידית</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Admin Emails Tab */}
          <TabsContent value="admins">
            <div className="grid gap-6">
              {/* Add Admin */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCog className="w-5 h-5 text-[#C5A45A]" />
                    הוספת אדמין דרך Google
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      type="email"
                      placeholder="user@example.com"
                      value={newAdminEmail}
                      onChange={(e) => setNewAdminEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addAdminEmail()}
                      className="h-12 flex-1"
                      data-testid="new-admin-email"
                    />
                    <Button
                      onClick={addAdminEmail}
                      className="h-12 px-6 bg-[#C5A45A] hover:bg-[#B8953D]"
                      data-testid="add-admin-btn"
                    >
                      <Plus className="w-4 h-4 ml-2" />
                      הוסף אדמין
                    </Button>
                  </div>
                  <p className="text-xs text-[#64748B] mt-3">
                    האדמין החדש יוכל להתחבר עם חשבון Google שלו לכתובת המייל הזאת
                  </p>
                </CardContent>
              </Card>

              {/* Admins List */}
              <Card>
                <CardHeader>
                  <CardTitle>אדמינים מורשים ({adminEmails.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {adminEmails.length === 0 ? (
                    <div className="text-center py-8 text-[#64748B]">
                      אין אדמינים
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {adminEmails.map((admin) => (
                        <div
                          key={admin.email}
                          className="flex items-center justify-between p-4 rounded-lg border border-[#E2E8F0] hover:bg-[#F8FAFC]"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#C5A45A]/10 flex items-center justify-center">
                              <Shield className="w-5 h-5 text-[#C5A45A]" />
                            </div>
                            <div>
                              <p className="font-medium text-[#0F172A]">{admin.email}</p>
                              {admin.is_primary && (
                                <span className="text-xs px-2 py-0.5 bg-[#C5A45A]/10 text-[#C5A45A] rounded">
                                  אדמין ראשי
                                </span>
                              )}
                            </div>
                          </div>
                          {!admin.is_primary && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAdminEmail(admin.email)}
                              className="text-[#EF4444] hover:bg-[#EF4444]/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info */}
              <Card className="bg-[#F0F9FF] border-[#38BDF8]/20">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Settings className="w-5 h-5 text-[#38BDF8] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-[#475569]">
                      <p className="font-medium text-[#0F172A] mb-1">איך זה עובד?</p>
                      <ul className="space-y-1">
                        <li>• הוסיפו כתובת Gmail (או חשבון Google עסקי)</li>
                        <li>• האדמין החדש נכנס לממשק ולוחץ "התחברות דרך Google"</li>
                        <li>• רק כתובות ברשימה כאן מקבלות גישה</li>
                        <li>• האדמין הראשי (מייסד) לא ניתן להסרה</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
